import { IAuthRepository } from '../../domain/repositories/IAuthRepository';
import { User } from '../../domain/entities/User';
import { Email } from '../../domain/value-objects/Email';
import type { UserRole } from '../../domain/value-objects/UserRole';
import { fromDatabaseRole } from '../../domain/value-objects/UserRole';
import { UserPreferences } from '../../domain/value-objects/UserPreferences';
import { supabase } from '@shared/config/supabase';

/**
 * Supabase Authentication Repository
 * Implements IAuthRepository using Supabase as the backend
 *
 * Database Schema Expected:
 * Table: users
 * - id (uuid, PK)
 * - workspace_id (uuid, FK)
 * - google_id (text, nullable)
 * - email (text)
 * - full_name (text)
 * - role (text: DIRECTOR, TEACHER, STUDENT)
 * - avatar (text, nullable)
 * - created_at (timestamp)
 * - updated_at (timestamp)
 */
export class SupabaseAuthRepository implements IAuthRepository {
  /**
   * Authenticate user with email and password
   */
  async login(email: string, password: string): Promise<User> {
    // Just trigger the sign in - don't wait for response
    // The auth state listener in AuthContext will handle the rest
    const signInPromise = supabase.auth.signInWithPassword({
      email,
      password,
    });

    // Return a dummy user - the real user will be set by the auth listener
    // This is a workaround for Supabase's hanging promises
    return new Promise((resolve, reject) => {
      // Give it 2 seconds to process
      setTimeout(async () => {
        try {
          // Try to check if there's an error
          const result = await Promise.race([
            signInPromise,
            new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 100))
          ]);

          // If we got here, check for errors
          const authResult = result as { error?: { message: string } };
          if (authResult.error) {
            reject(new Error(authResult.error.message));
          } else {
            // Return a dummy user - the listener will set the real one
            resolve({} as User);
          }
        } catch {
          // Timeout or no response - that's OK, the listener will handle it
          resolve({} as User);
        }
      }, 2000);
    });
  }

  /**
   * Register a new user with email and password
   */
  async register(email: string, password: string, fullName: string, role: UserRole, workspaceId: string): Promise<User> {
    // Create user in Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
    });

    if (authError) {
      throw new Error(`Registration failed: ${authError.message}`);
    }

    if (!authData.user) {
      throw new Error('Registration failed: No user returned');
    }

    // The trigger 'on_auth_user_created' already creates a user profile with default values.
    // We need to UPDATE it with the correct data instead of INSERT.
    const { data, error } = await supabase
      .from('users')
      .update({
        workspace_id: parseInt(workspaceId),
        full_name: fullName,
        role: role.toUpperCase(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', authData.user.id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update user profile: ${error.message}`);
    }

    return this.mapToUser(data);
  }

  /**
   * Find user by ID
   */
  async findById(id: string): Promise<User | null> {
    const MAX_RETRIES = 3;
    const BASE_DELAY = 1000;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        console.log(`SupabaseAuthRepository.findById: Attempt ${attempt}/${MAX_RETRIES} for ID:`, id);

        const queryPromise = supabase
          .from('users')
          .select('*')
          .eq('id', id)
          .single();

        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('findById query timeout after 5s')), 5000)
        );

        const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

        console.log('SupabaseAuthRepository.findById: Query completed', { data: !!data, error: error?.message || null });

        if (error) {
          if (error.code === 'PGRST116') {
            // User genuinely not found - no retry needed
            console.log('SupabaseAuthRepository.findById: User not found (PGRST116)');
            return null;
          }
          console.error('Failed to fetch user:', error);
          // DB error but not "not found" - retry
          if (attempt < MAX_RETRIES) {
            const delay = BASE_DELAY * attempt;
            console.log(`SupabaseAuthRepository.findById: Retrying in ${delay}ms...`);
            await new Promise(r => setTimeout(r, delay));
            continue;
          }
          return null;
        }

        if (!data) {
          console.log('SupabaseAuthRepository.findById: No data returned');
          return null;
        }

        console.log('SupabaseAuthRepository.findById: Mapping user data');
        return this.mapToUser(data);
      } catch (err) {
        console.error(`SupabaseAuthRepository.findById: Error (attempt ${attempt}):`, err);
        if (attempt < MAX_RETRIES) {
          const delay = BASE_DELAY * attempt;
          console.log(`SupabaseAuthRepository.findById: Retrying in ${delay}ms...`);
          await new Promise(r => setTimeout(r, delay));
          continue;
        }
        // All retries exhausted - throw to distinguish from "not found"
        throw new Error('Unable to verify user profile. Please check your connection.');
      }
    }
    return null;
  }

  /**
   * Find user by email
   */
  async findByEmail(email: string): Promise<User | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .single();

    if (error || !data) {
      return null;
    }

    return this.mapToUser(data);
  }

  /**
   * Find user by Google ID
   */
  async findByGoogleId(googleId: string): Promise<User | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('google_id', googleId)
      .single();

    if (error || !data) {
      return null;
    }

    return this.mapToUser(data);
  }

  /**
   * Initiate Google OAuth login flow
   * Redirects to Google OAuth consent screen
   */
  async loginWithGoogle(): Promise<void> {
    const redirectUrl = `${window.location.origin}/dashboard`;
    console.log('loginWithGoogle: Iniciando OAuth con redirect a:', redirectUrl);

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        queryParams: {
          access_type: 'offline',
          prompt: 'select_account',
        },
      },
    });

    console.log('loginWithGoogle: Respuesta de Supabase:', { data, error });

    if (error) {
      console.error('loginWithGoogle: Error:', error);
      throw new Error(`Google login failed: ${error.message}`);
    }

    // Si hay una URL, redirigir manualmente (algunos navegadores bloquean redirecciones automáticas)
    if (data?.url) {
      console.log('loginWithGoogle: Redirigiendo a:', data.url);
      window.location.href = data.url;
    }
  }

  /**
   * Create or update user profile after OAuth login
   * Called when a user signs in via OAuth and may not have a profile yet
   */
  async createOrUpdateOAuthUser(
    authUserId: string,
    email: string,
    fullName: string,
    googleId: string,
    avatar?: string
  ): Promise<User> {
    // Check if user already exists by email
    const existingUser = await this.findByEmail(email);

    if (existingUser) {
      // Update existing user with Google ID if not set
      if (!existingUser.googleId) {
        const updatedUser = new User({
          ...existingUser.toPlainObject(),
          googleId,
          avatar: avatar || existingUser.toPlainObject().avatar,
        });
        return this.update(updatedUser);
      }
      return existingUser;
    }

    // Create new user - default role is 'student', workspace will need to be set later
    const newUser = new User({
      id: authUserId,
      workspaceId: '1', // Default workspace - user can change later
      googleId,
      email: new Email(email),
      passwordHash: null,
      fullName,
      role: 'student',
      avatar,
    });

    return this.create(newUser);
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User | null> {
    console.log('SupabaseAuthRepository: Getting current user...');

    try {
      // Get current session from Supabase Auth with timeout
      const sessionPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('getSession timeout')), 10000)
      );

      const { data: { session } } = await Promise.race([sessionPromise, timeoutPromise]);
      console.log('SupabaseAuthRepository: Session:', session ? 'Found' : 'Not found');

      if (!session?.user) {
        console.log('SupabaseAuthRepository: No session user');
        return null;
      }

      console.log('SupabaseAuthRepository: Session user ID:', session.user.id);

      // Fetch user profile
      const user = await this.findById(session.user.id);
      console.log('SupabaseAuthRepository: User profile:', user ? 'Found' : 'Not found');

      return user;
    } catch (error) {
      console.error('SupabaseAuthRepository: getCurrentUser error:', error);
      return null;
    }
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    const { error } = await supabase.auth.signOut();

    if (error) {
      throw new Error(`Logout failed: ${error.message}`);
    }
  }

  /**
   * Get all users in a workspace
   */
  async findByWorkspaceId(workspaceId: string): Promise<User[]> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('workspace_id', parseInt(workspaceId));

    if (error) {
      throw new Error(`Failed to fetch users: ${error.message}`);
    }

    return (data || []).map(this.mapToUser);
  }

  /**
   * Get users by role in a workspace
   */
  async findByRole(workspaceId: string, role: UserRole): Promise<User[]> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('workspace_id', parseInt(workspaceId))
      .eq('role', role.toUpperCase());

    if (error) {
      throw new Error(`Failed to fetch users by role: ${error.message}`);
    }

    return (data || []).map(this.mapToUser);
  }

  /**
   * Create a new user
   */
  async create(user: User): Promise<User> {
    const userData = {
      id: user.id,
      workspace_id: parseInt(user.workspaceId ?? '0'),
      google_id: user.googleId,
      email: user.email.value,
      full_name: user.fullName,
      role: user.role.toUpperCase(),
      avatar: user.toPlainObject().avatar,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };


    const { data, error } = await supabase
      .from('users')
      .insert(userData)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create user: ${error.message}`);
    }

    return this.mapToUser(data);
  }

  /**
   * Update an existing user
   */
  async update(user: User): Promise<User> {
    const userData = {
      workspace_id: parseInt(user.workspaceId ?? '0'),
      google_id: user.googleId,
      email: user.email.value,
      full_name: user.fullName,
      role: user.role.toUpperCase(),
      avatar: user.toPlainObject().avatar,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('users')
      .update(userData)
      .eq('id', user.id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update user: ${error.message}`);
    }

    return this.mapToUser(data);
  }

  /**
   * Delete a user
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', id);

    if (error) {
      throw new Error(`Failed to delete user: ${error.message}`);
    }
  }

  /**
   * Map database row to User entity
   */
  private mapToUser(data: Record<string, unknown>): User {
    return new User({
      id: data.id as string,
      workspaceId: String(data.workspace_id),
      googleId: data.google_id as string | null,
      email: new Email(data.email as string),
      passwordHash: null, // We don't expose password hashes
      fullName: data.full_name as string,
      role: fromDatabaseRole(data.role as string),
      avatar: data.avatar as string | undefined,
      preferences: UserPreferences.fromJSON(data.preferences as Record<string, unknown> | null | undefined),
      mustChangePassword: (data.must_change_password as boolean | undefined) ?? false,
      createdAt: data.created_at ? new Date(data.created_at as string) : undefined,
      updatedAt: data.updated_at ? new Date(data.updated_at as string) : undefined,
    });
  }
}
